import { useRef, useState } from 'react';
import classnames from 'classnames';
import moment from 'moment/min/moment-with-locales';
import { useTranslation } from 'react-i18next';
import { prettyFloatMoney } from '@/helpers/utils';
import {
  DATE_FORMAT_D,
  DATE_FORMAT_D_MMMM,
  DATE_FORMAT_DD_MMMM,
  DATE_FORMAT_M,
} from '@/helpers/common';
import { ACCOUNT_TYPES, EMPTY_DASH } from '@/helpers/constants';
import { useGetFinances } from '@/query/queries/cash/useGetFinances';
import { useGetMe } from '@/hooks/shared/useGetMe';
import { LanguageSwitcher } from '@/components/LanguageSwitcher';
import { useOutsideClick } from '@/hooks/useOutsideClick';
import { safeDecimal } from '@/helpers/number';
import { MyTooltip } from '@/webkit/MyTooltip';
import { useUserStore } from '@/store/userStore';
import { NextRepaymentTooltip } from './NextRepaymentTooltip';
import { CashTooltip } from './CashTooltip';
import { useGetFundraisingDates } from '@/query/queries/fundraising/useGetFundraisingDates';
import IconWarning from './icon_warning.svg';
import { AccountTypeSwitch } from './switch';
import './index.scss';

export const TopHeader = () => {
  const { t } = useTranslation('components');
  const [show, setShow] = useState(false);
  const { accountType } = useUserStore();
  const { isMainFounder, isFinance, isCombinedMode } = useGetMe();
  const { data: finances } = useGetFinances();
  const ref = useRef();
  const { data: fundraisingDates } = useGetFundraisingDates();
  useOutsideClick(ref, () => setShow(false), show);

  const getFundraisingState = () => {
    if (!fundraisingDates) {
      return '';
    }

    if (fundraisingDates.is_active && fundraisingDates.end_date) {
      return (
        <span>
          {t('top_header.active_fundraising_until')}{' '}
          <span className="f-body-medium" style={{ color: 'var(--Black-500-Regular, #1A2833)' }}>
            {moment(fundraisingDates.end_date).format(DATE_FORMAT_DD_MMMM)}
          </span>
        </span>
      );
    }

    if (!fundraisingDates.is_active && fundraisingDates.start_date && fundraisingDates.end_date) {
      if (
        moment(fundraisingDates.end_date).format(DATE_FORMAT_M) ===
        moment(fundraisingDates.start_date).format(DATE_FORMAT_M)
      ) {
        return (
          <span>
            {t('top_header.next_fundraising')}{' '}
            <span className="f-body-medium" style={{ color: 'var(--Black-500-Regular, #1A2833)' }}>
              {moment(fundraisingDates.start_date).format(DATE_FORMAT_D)} -{' '}
              {moment(fundraisingDates.end_date).format(DATE_FORMAT_D_MMMM)}
            </span>
          </span>
        );
      }
      return (
        <span>
          {t('top_header.next_fundraising')}{' '}
          <span className="f-body-medium" style={{ color: 'var(--Black-500-Regular, #1A2833)' }}>
            {moment(fundraisingDates.start_date).format(DATE_FORMAT_D_MMMM)} -
            {moment(fundraisingDates.end_date).format(DATE_FORMAT_D_MMMM)}
          </span>
        </span>
      );
    }
    // Fundraising not set on Database
    return '';
  };

  const fundraisingState = getFundraisingState();
  const finance = finances && finances[accountType];
  const businessAccount = accountType === ACCOUNT_TYPES.business;

  const frozenAmount = finance?.frozen_amount || 0;
  const showWarning =
    businessAccount && finance && finance.next_payment > finance.cash + frozenAmount;

  return (
    <div className="top-header">
      <div className="container-inner">
        <div className="top-header__inner">
          <div className="top-header__dates f-small-regular">{fundraisingState}</div>
          <div className="top-header__summary">
            {accountType === ACCOUNT_TYPES.business && (
              <div className="top-header__payment">
                <span className="f-body-regular">{t('top_header.next_repayment')}:</span>
                <MyTooltip
                  renderContent={() => <NextRepaymentTooltip showWarning={showWarning} />}
                  renderElement={() => (
                    <div className="top-header__payment_text">
                      <span className={classnames('f-body-medium black', finances && 'underlined')}>
                        {finance ? prettyFloatMoney(finance.next_payment) : EMPTY_DASH}
                      </span>
                      {showWarning && (
                        <img
                          src={IconWarning}
                          alt="warning"
                          width={16}
                          height={16}
                          style={{ minWidth: '16px' }}
                        />
                      )}
                    </div>
                  )}
                />
              </div>
            )}
            <div className="top-header__cash" ref={ref} onClick={() => setShow(!show)}>
              <span className="f-body-regular">{t('top_header.cash_account')}:</span>
              <MyTooltip
                renderContent={() => <CashTooltip finance={finance} />}
                renderElement={() => (
                  <span className={classnames('f-body-medium black', finances && 'underlined')}>
                    {finance
                      ? prettyFloatMoney(
                          safeDecimal(finance.cash)
                            .add(safeDecimal(finance?.frozen_amount))
                            .toNumber(),
                        )
                      : EMPTY_DASH}
                  </span>
                )}
              />
            </div>
            {(isMainFounder || isFinance) && isCombinedMode && <AccountTypeSwitch />}
            <LanguageSwitcher />
          </div>
        </div>
      </div>
    </div>
  );
};
